import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

export type ToggleWithAnimationState =
  | 'opened'
  | 'opening'
  | 'closed'
  | 'closing';

export interface UseToggleWithAnimationArgs {
  isOpen: boolean;
  transitionDuration?: number;
}

export interface UseToggleWithAnimationReturn {
  state: ToggleWithAnimationState;
  transitionDuration: number;
}

const isCloseState = (state: ToggleWithAnimationState): boolean =>
  state === 'closed' || state === 'closing';

export const useToggleWithAnimation = ({
  transitionDuration = 200,
  isOpen,
}: UseToggleWithAnimationArgs): UseToggleWithAnimationReturn => {
  const closedTimeoutId: MutableRefObject<number | undefined> = useRef();
  const openedTimeoutId: MutableRefObject<number | undefined> = useRef();
  const [state, setState] = useState<ToggleWithAnimationState>(() =>
    isOpen ? 'opened' : 'closed'
  );

  useEffect(() => {
    if (state === 'opening') {
      openedTimeoutId.current = window.setTimeout(() => {
        setState('opened');
      }, transitionDuration);
    }

    if (state === 'closing') {
      closedTimeoutId.current = window.setTimeout(() => {
        setState('closed');
      }, transitionDuration);
    }

    return () => {
      if (closedTimeoutId.current) clearTimeout(closedTimeoutId.current);
      if (openedTimeoutId.current) clearTimeout(openedTimeoutId.current);
    };
  }, [state, transitionDuration]);

  useEffect(() => {
    if (isOpen) {
      setState((state) => (isCloseState(state) ? 'opening' : state));
    } else {
      setState((state) => (isCloseState(state) ? state : 'closing'));
    }
  }, [isOpen]);

  return {
    state,
    transitionDuration,
  };
};

// @deprecated
export const useFadeToggle = useToggleWithAnimation;
